// File: src/components/Answer/WebCitations.tsx
//
// Compact citation chip cluster (favicon row) with click-to-open popover, used to
// surface sources returned by the `web_research` tool. Inspired by ChatGPT/Claude.

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./WebCitations.module.css";

export type WebCitationItem = {
    source?: string;
    url?: string;
    claim?: string;
};

interface Props {
    items: WebCitationItem[];
    /** Hard cap on chips shown inline before "+N more" overflow indicator. */
    maxInlineChips?: number;
    className?: string;
}

/**
 * Resolve a Google s2/favicons URL for a citation URL. Falls back to an empty
 * string if the URL is missing/invalid; the component will render a textual
 * fallback chip in that case.
 */
function faviconUrlFor(targetUrl: string | undefined, size: number): string {
    if (!targetUrl) return "";
    try {
        const parsed = new URL(targetUrl);
        return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(parsed.hostname)}&sz=${size}`;
    } catch {
        return "";
    }
}

function safeHostname(targetUrl: string | undefined): string {
    if (!targetUrl) return "";
    try {
        return new URL(targetUrl).hostname.replace(/^www\./, "");
    } catch {
        return "";
    }
}

function deriveSourceLabel(item: WebCitationItem): string {
    if (item.source && item.source.trim()) return item.source.trim();
    return safeHostname(item.url) || "Source";
}

export const WebCitations: React.FC<Props> = ({ items, maxInlineChips = 4, className }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        if (!open) return;
        const handleClickAway = (e: MouseEvent) => {
            if (!containerRef.current) return;
            if (containerRef.current.contains(e.target as Node)) return;
            setOpen(false);
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handleClickAway);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handleClickAway);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [open]);

    // Position the popover above the chip cluster, flipping below if there's no room.
    useLayoutEffect(() => {
        if (!open) return;
        if (!containerRef.current || !popoverRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const popH = popoverRef.current.offsetHeight;
        const spaceAbove = rect.top;
        const placeAbove = spaceAbove > popH + 12;
        if (placeAbove) {
            setPopoverStyle({ bottom: "calc(100% + 8px)", top: "auto" });
        } else {
            setPopoverStyle({ top: "calc(100% + 8px)", bottom: "auto" });
        }
    }, [open]);

    if (!items || items.length === 0) return null;

    const visibleChips = items.slice(0, maxInlineChips);
    const overflowCount = Math.max(0, items.length - visibleChips.length);

    return (
        <div className={`${styles.container} ${className || ""}`} ref={containerRef}>
            <button
                type="button"
                className={styles.chipCluster}
                aria-expanded={open}
                aria-label={`${items.length} source${items.length === 1 ? "" : "s"}`}
                onClick={() => setOpen(prev => !prev)}
            >
                {visibleChips.map((item, idx) => {
                    const favicon = faviconUrlFor(item.url, 32);
                    const label = deriveSourceLabel(item);
                    return (
                        <span key={`chip-${idx}`} className={styles.chip} title={label}>
                            {favicon ? (
                                <img className={styles.chipFavicon} src={favicon} alt="" loading="lazy" onError={e => ((e.target as HTMLImageElement).style.display = "none")} />
                            ) : (
                                <span className={styles.chipFallback} aria-hidden="true">
                                    {label.charAt(0).toUpperCase()}
                                </span>
                            )}
                        </span>
                    );
                })}
                {overflowCount > 0 && <span className={styles.overflowBadge}>+{overflowCount}</span>}
                <span className={styles.sourceCount}>
                    {items.length} {items.length === 1 ? "source" : "sources"}
                </span>
            </button>

            {open && (
                <div ref={popoverRef} className={styles.popover} style={popoverStyle} role="dialog" aria-label="Sources">
                    <div className={styles.popoverHeader}>
                        Sources ({items.length})
                    </div>
                    <ul className={styles.popoverList}>
                        {items.map((item, idx) => {
                            const favicon = faviconUrlFor(item.url, 64);
                            const label = deriveSourceLabel(item);
                            const host = safeHostname(item.url);
                            const isClickable = !!item.url;
                            const inner = (
                                <>
                                    <span className={styles.popoverFavicon}>
                                        {favicon ? (
                                            <img src={favicon} alt="" loading="lazy" onError={e => ((e.target as HTMLImageElement).style.display = "none")} />
                                        ) : (
                                            <span className={styles.popoverFaviconFallback}>{label.charAt(0).toUpperCase()}</span>
                                        )}
                                    </span>
                                    <span className={styles.popoverText}>
                                        <span className={styles.popoverSource}>{label}</span>
                                        {host && <span className={styles.popoverHost}>{host}</span>}
                                        {item.claim && <span className={styles.popoverClaim}>{item.claim}</span>}
                                    </span>
                                </>
                            );
                            return (
                                <li key={`source-${idx}`} className={styles.popoverItem}>
                                    {isClickable ? (
                                        <a className={styles.popoverLink} href={item.url} target="_blank" rel="noopener noreferrer">
                                            {inner}
                                        </a>
                                    ) : (
                                        <span className={styles.popoverLink}>{inner}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default WebCitations;
